import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MangaCard from '@/components/base/MangaCard';
import { mangas, categories, testimonials, faqs, siteInfo } from '@/mocks/mangaData';

const featuredMangas = mangas.filter((m) => m.featured);
const latestMangas = mangas.slice(0, 8);

const platformFeatures = [
  { icon: 'ri-scroll-to-bottom-line', title: '下拉式翻页', desc: '无缝连续阅读，一气呵成看完整话' },
  { icon: 'ri-flashlight-line', title: '极速加载', desc: 'CDN 加速 + 图片预加载，秒开不等待' },
  { icon: 'ri-money-cny-circle-line', title: '全站免费', desc: '所有漫画免费阅读，无需充值会员' },
  { icon: 'ri-calendar-check-line', title: '每日更新', desc: '热门漫画每日准点更新，追更不停歇' },
];

const stats = [
  { value: '12万+', label: '漫画资源' },
  { value: '350万+', label: '日活跃用户' },
  { value: '0.3s', label: '平均加载速度' },
  { value: '99.9%', label: '服务可用率' },
];

export default function Home() {
  useEffect(() => {
    document.title = '健身教练漫画-免费阅读漫画下拉式-4399漫画网';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', '4399漫画网凭借快速的加载技术，让您免费下拉式阅读《健身教练漫画》时几乎无需等待，享受极速翻页的畅快体验。海量免费漫画资源，每日更新。');
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', '4399漫画,下拉式阅读,免费漫画,加载速度快');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', 'http://chigua52.cn/');
  }, []);

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': '4399漫画网',
    'url': 'http://chigua52.cn/',
    'description': siteInfo.siteDescription,
    'potentialAction': {
      '@type': 'SearchAction',
      'target': 'http://chigua52.cn/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    'name': '4399漫画网',
    'url': 'http://chigua52.cn/',
    'description': siteInfo.siteDescription,
    'foundingDate': '2012',
    'contactPoint': {
      '@type': 'ContactPoint',
      'email': siteInfo.email,
      'telephone': siteInfo.phone,
      'contactType': 'customer service',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((faq) => ({
      '@type': 'Question',
      'name': faq.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': faq.answer,
      },
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': '首页', 'item': 'http://chigua52.cn/' },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <header className="relative w-full h-[520px] md:h-[680px] flex flex-col items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20artistic%20abstract%20illustration%20with%20soft%20gradients%20in%20orange%20coral%20and%20cream%20tones%2C%20manga%20comic%20book%20reading%20vibe%2C%20stylized%20geometric%20patterns%20with%20subtle%20book%20and%20page%20motifs%2C%20warm%20inviting%20atmosphere%2C%20minimalist%20scandinavian%20style%20with%20Japanese%20manga%20influence%2C%20soft%20lighting%2C%20clean%20composition&width=1800&height=900&seq=hero-bg-home&orientation=landscape"
            alt="4399漫画网 - 免费漫画下拉式阅读"
            title="4399漫画网 - 免费漫画下拉式阅读平台"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="relative z-10 text-center px-4 w-full max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white font-heading leading-tight">
              下拉式阅读，<br />让漫画更自由
            </h1>
            <p className="mt-5 text-base md:text-lg text-white/80 max-w-xl mx-auto leading-relaxed">
              4399漫画网，海量免费漫画在线下拉式阅读
            </p>
            <p className="mt-2 text-sm md:text-base text-white/60 max-w-xl mx-auto">
              极速加载，无缝翻页，每日更新，畅享最流畅的漫画阅读体验
            </p>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/all"
                className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-3 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
              >
                立即开始阅读
              </Link>
              <Link
                to="/manga/1/chapter/248"
                className="bg-white/15 hover:bg-white/25 text-white border border-white/30 px-8 py-3 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >
                热门推荐 → 健身教练
              </Link>
            </div>
          </div>
          <div className="absolute -bottom-1 left-4 right-4 md:left-8 md:right-8 z-10">
            <div className="max-w-5xl mx-auto bg-background-50 rounded-t-2xl overflow-hidden h-12 md:h-16 shadow-[0_-4px_24px_rgba(0,0,0,0.08)]"></div>
          </div>
        </header>

        <main>
          <section className="py-10 md:py-16 px-4 md:px-6 mx-auto max-w-7xl">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                热门推荐
              </h2>
              <p className="mt-2 text-sm text-foreground-500">千万读者都在追的热门漫画</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
              {featuredMangas.map((manga) => (
                <MangaCard key={manga.id} {...manga} />
              ))}
            </div>
            <div className="text-center mt-8">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer"
              >
                查看更多热门漫画 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </section>

          <section className="py-12 md:py-16 px-4 md:px-6 bg-secondary-50/80">
            <div className="mx-auto max-w-7xl">
              <div className="text-center mb-8 md:mb-12">
                <span className="inline-block bg-white px-4 py-1.5 rounded-full text-xs font-medium text-foreground-600 border border-background-200/70">
                  为什么选择我们
                </span>
                <h2 className="mt-4 text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                  极致的漫画<br />阅读体验
                </h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {platformFeatures.map((feature) => (
                  <div key={feature.title} className="text-center p-5">
                    <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center bg-secondary-100 rounded-xl">
                      <i className={`${feature.icon} text-xl text-secondary-600`}></i>
                    </div>
                    <h3 className="text-sm font-semibold text-foreground-950 mb-1">
                      {feature.title}
                    </h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">
                      {feature.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-10 md:py-14 px-4 md:px-6 mx-auto max-w-7xl">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
              {stats.map((stat) => (
                <div key={stat.label}>
                  <p className="text-3xl md:text-4xl font-black text-secondary-700 font-heading">
                    {stat.value}
                  </p>
                  <p className="text-xs text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="py-10 md:py-16 px-4 md:px-6 mx-auto max-w-7xl">
            <div className="flex items-center justify-between mb-8 md:mb-12">
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                  最新更新
                </h2>
                <p className="mt-1 text-sm text-foreground-500">每日零点准时更新热门章节</p>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line ml-1"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {latestMangas.map((manga) => (
                <MangaCard key={manga.id} {...manga} />
              ))}
            </div>
          </section>

          <section className="py-12 md:py-16 px-4 md:px-6 mx-auto max-w-7xl">
            <div className="bg-foreground-900 rounded-2xl overflow-hidden flex flex-col lg:flex-row">
              <div className="lg:w-2/5 relative min-h-[280px] lg:min-h-[400px]">
                <div className="absolute lg:-left-8 lg:top-1/2 lg:-translate-y-1/2 inset-0 lg:inset-auto w-full lg:w-[140%] h-full lg:h-[120%]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Anime%20manga%20style%20portrait%20of%20a%20young%20enthusiastic%20manga%20reader%20holding%20a%20phone%20reading%20comics%2C%20warm%20cozy%20indoor%20setting%2C%20soft%20lighting%2C%20friendly%20expression%2C%20illustration%20style&width=600&height=800&seq=testimonial-avatar&orientation=portrait"
                    alt="4399漫画网忠实读者"
                    title="4399漫画网 - 读者反馈"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
              </div>
              <div className="lg:w-3/5 p-8 md:p-10 lg:p-12 flex flex-col justify-center">
                <span className="text-5xl text-accent-300 font-serif leading-none mb-3">&ldquo;</span>
                <blockquote className="text-white text-base md:text-lg leading-relaxed mb-5">
                  {testimonials[0].content}
                </blockquote>
                <div className="mb-5">
                  <p className="text-white text-sm font-semibold">{testimonials[0].name}</p>
                  <p className="text-white/50 text-xs">{testimonials[0].role}</p>
                </div>
                <Link
                  to="/about"
                  className="inline-flex bg-accent-500 hover:bg-accent-600 text-foreground-950 px-5 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap w-fit"
                >
                  查看更多评价
                </Link>
              </div>
            </div>
          </section>

          <section className="relative py-16 md:py-24 px-4 md:px-6 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20artistic%20illustration%20of%20manga%20reading%20atmosphere%2C%20soft%20orange%20coral%20and%20cream%20gradient%20abstract%20composition%2C%20cozy%20reading%20vibe%20with%20subtle%20page%20and%20book%20textures%2C%20warm%20inviting%20color%20palette%2C%20minimalist%20modern%20design&width=1800&height=900&seq=cta-bg-home&orientation=landscape"
              alt="4399漫画网 - 立即开始免费阅读"
              title="4399漫画网 - 免费阅读漫画"
              className="absolute inset-0 w-full h-full object-cover object-center"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
            <div className="relative z-10 text-center max-w-2xl mx-auto">
              <h2 className="text-2xl md:text-4xl font-bold text-white font-heading">
                现在就开始你的漫画之旅
              </h2>
              <p className="mt-4 text-sm md:text-base text-white/70">
                海量漫画资源，极速下拉式阅读体验
              </p>
              <p className="mt-1 text-sm md:text-base text-white/60">
                完全免费，无需注册，立即畅读
              </p>
              <Link
                to="/category/all"
                className="mt-7 inline-flex items-center gap-3 bg-foreground-950 hover:bg-foreground-800 text-white px-8 py-3.5 rounded-full text-sm font-semibold transition-all cursor-pointer whitespace-nowrap"
              >
                <span className="w-7 h-7 flex items-center justify-center bg-accent-500 rounded-full">
                  <i className="ri-play-fill text-white text-xs"></i>
                </span>
                免费阅读
                <i className="ri-arrow-right-up-line text-lg"></i>
              </Link>
            </div>
          </section>

          <section className="py-10 md:py-16 px-4 md:px-6 mx-auto max-w-7xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading text-center mb-8 md:mb-12">
              漫画分类
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
              {categories.filter((c) => c.id !== 'all').map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group bg-background-100 hover:bg-primary-50 rounded-xl p-5 text-center transition-colors cursor-pointer border border-transparent hover:border-primary-200"
                >
                  <h3 className="text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-400 mt-1">
                    {mangas.filter((m) => m.category === cat.id).length} 部作品
                  </p>
                </Link>
              ))}
            </div>
          </section>

          <section className="py-10 md:py-16 px-4 md:px-6 mx-auto max-w-7xl">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading text-center mb-8 md:mb-12">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {faqs.map((faq, index) => (
                <details key={index} className="group bg-background-100 rounded-xl border border-background-200/70">
                  <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium text-foreground-800 list-none">
                    {faq.question}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                </details>
              ))}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}